/**
 * Redact bearer credentials that travel in URL paths before a URL reaches
 * durable storage — request logs, telemetry rows, the error tracker's
 * `sample_url`, GitHub issue bodies.
 *
 * This is the ONE list of credential routes. The gateway's request log and
 * client-error intake, billing's unhandled-error log, the error tracker and
 * v4-frontend's client-error telemetry all import it. Tests fail when a
 * v4-frontend `[token]` page, or a frontend API call that puts a token in its
 * path, is missing from it.
 *
 * Credential routes:
 *   - page routes whose FIRST path segment is one of TOKEN_ROUTE_SEGMENTS:
 *     `/claim/<claim token>`, `/remove/<removal token>`, `/f/<forward token>`,
 *     `/approve/<approval token>`, `/agreement/<agreement link token>`;
 *   - the agreement-link API, `/api/agreement-links/<agreement link token>/…`
 *     (the signer page's every call, including `/otp/verify`), and the path
 *     the gateway forwards it to, billing's `/agreement-links/<token>/…`
 *     (`http://internal/…`, the billing invocation's logged URL);
 *   - the forward-feedback API, `/api/forwards/<forward token>/…`, and the
 *     approval magic-link API, `/api/approvals/<approval token>` (the token is
 *     the auth; tenant approval routes live under `/api/tenants/`);
 *   - the claim-invite one-click unsubscribe API,
 *     `/api/v1/claim-invites/unsubscribe/<removal token>` (RFC 8058 puts the
 *     token in the List-Unsubscribe URL);
 *   - the job-alert one-click unsubscribe API,
 *     `/api/v1/job-alerts/unsubscribe/<token>`, and its confirmation page,
 *     `/alerts/unsubscribe/<token>` (@vhire/shared/service-types/job-alerts).
 *
 * Only the credential segment becomes `[token]`; later segments survive. On a
 * redacted URL the query string and fragment are dropped wholesale: nothing on
 * them is worth keeping, and a link that carried the token as a query parameter
 * would otherwise re-leak it. Everything else — including API paths that merely
 * contain a route word, like `/api/v1/claim/preview` — is returned untouched.
 */

/** First path segments whose next segment is a live credential. */
export const TOKEN_ROUTE_SEGMENTS: ReadonlySet<string> = new Set(['claim', 'remove', 'f', 'approve', 'agreement']);

/** Multi-segment credential routes as path segments, token excluded. */
const CREDENTIAL_ROUTE_PREFIXES: ReadonlyArray<readonly string[]> = [
  ['', 'api', 'agreement-links'],
  ['', 'agreement-links'],
  ['', 'api', 'forwards'],
  ['', 'api', 'approvals'],
  ['', 'api', 'v1', 'claim-invites', 'unsubscribe'],
  ['', 'api', 'v1', 'job-alerts', 'unsubscribe'],
  ['', 'alerts', 'unsubscribe'],
];

function credentialSegmentIndex(segments: string[]): number {
  if (segments[0] !== '') return -1;
  if (TOKEN_ROUTE_SEGMENTS.has(segments[1]) && segments[2]) return 2;
  const prefix = CREDENTIAL_ROUTE_PREFIXES.find(
    (route) => Boolean(segments[route.length]) && route.every((segment, index) => segments[index] === segment),
  );
  return prefix ? prefix.length : -1;
}

/** Replace the credential segment of a token-bearing path or absolute URL with `[token]`. */
export function redactTokenPaths(raw: string): string {
  if (!raw) return raw;
  let origin = '';
  let rest = raw;
  const schemeEnd = raw.indexOf('://');
  if (schemeEnd !== -1) {
    const pathStart = raw.indexOf('/', schemeEnd + 3);
    if (pathStart === -1) return raw; // origin only — no path to redact
    origin = raw.slice(0, pathStart);
    rest = raw.slice(pathStart);
  }
  const queryStart = rest.search(/[?#]/);
  const pathname = queryStart === -1 ? rest : rest.slice(0, queryStart);
  const segments = pathname.split('/');
  const index = credentialSegmentIndex(segments);
  if (index === -1) return raw;
  segments[index] = '[token]';
  return origin + segments.join('/');
}
